import { useState, type FC, type PropsWithChildren } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { toast } from 'sonner';

import Modal2FA from '@features/account-settings/profile/components/modal-2FA';
import { useStore } from '@/stores';
import { useUserLogoutMutation } from '@/features/auth/api/useUsersLogoutMutation';
import { env } from '@/config/env';

import { IncompleteContent } from './components/incomplete-content';
import { PendingContent } from './components/pending-content';
import { NoAccessAccountGroup } from './components/no-access-group-content';
import { EnabledContentOTP } from './components/enable-otp';

interface ContentProps extends PropsWithChildren {
  statusRegistered: string;
  isAccountGroup?: boolean;
}

const restrictedPaths = ['/account-group', '/group-commission'];

const Content: FC<ContentProps> = ({ children, statusRegistered, isAccountGroup }) => {
  const { location } = useRouterState();
  const profile = useStore((state) => state.profile);
  const [openDialog2FA, setOpenDialog2FA] = useState(false);
  const enableContentOtp = env.enableContentOtp === 'true';
  const is2FAEnabled = profile ? profile.is_login_otp_enabled : true;
  const shouldShow2FAGate = enableContentOtp && (!is2FAEnabled || openDialog2FA);
  const accessToken = useStore.getState().accessToken;
  const shouldCheckAccess = restrictedPaths.some((path) => location.pathname.startsWith(path));

  const { mutate: logoutUser, isPending: isLoggingOut } = useUserLogoutMutation({
    onSuccess: () => {
      localStorage.removeItem('dashboard-merchant-storage');
      window.location.replace('/auth/login');
    },
    onError: (err) => {
      toast.error('Logout failed', { description: err.message });
    },
  });

  const handleLogout = () => {
    if (!accessToken) {
      window.location.replace('/auth/login');
      return;
    }
    logoutUser({ access_token: accessToken });
  };

  if (shouldShow2FAGate) {
    return (
      <main className="content">
        <Modal2FA
          openModal={openDialog2FA}
          isEnabled={false}
          onClose={() => setOpenDialog2FA(false)}
        />
        {!is2FAEnabled && (
          <EnabledContentOTP
            onEnable2FA={() => setOpenDialog2FA(true)}
            onLogout={handleLogout}
            isLoggingOut={isLoggingOut}
          />
        )}
      </main>
    );
  }

  switch (statusRegistered) {
    case 'INCOMPLETE':
      return <IncompleteContent />;
    case 'PENDING':
      return <PendingContent />;
  }

  if (shouldCheckAccess && isAccountGroup === false) {
    return <NoAccessAccountGroup />;
  }

  return <main className="content">{children}</main>;
};

export default Content;
